<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Company profile</span>
      </div>
    </div>

    <div v-if="isLoadingProfile" class="px-6 py-12 flex justify-center items-center">
      <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <form v-else @submit.prevent="save" class="px-6 pb-5 pt-2">
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Accounting Types</label>
          <select v-model="form.accountingType"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            required>
            <option value="" disabled>Accounting Type</option>
            <option value="accrual">Accrual Accounting</option>
            <option value="cash">Cash Basis Accounting</option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Accounting Period</label>
          <select v-model="form.accountingPeriod"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            required>
            <option value="" disabled>Accounting Period</option>
            <option value="periodic">Periodic</option>
            <option value="calendar">Calender</option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Start Date</label>
          <input v-model="form.startDate"
            type="date"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            required />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">End Date</label>
          <input v-model="form.endDate"
            type="date"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            required />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Company Status</label>
          <select v-model="form.companyStatus"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            required>
            <option value="" disabled>Company Status</option>
            <option value="PLC">PLC</option>
            <option value="One Member PLC">One Member PLC</option>
            <option value="SOL">SOL</option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Tax Type</label>
          <select v-model="form.taxType"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            required>
            <option value="" disabled>Tax Type</option>
            <option value="Tax">Tax</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Posting Method</label>
          <select v-model="form.postingMethod"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            required>
            <option value="" disabled>Posting Method</option>
            <option value="real_time">Real Time</option>
            <option value="batch">Batch</option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Backup</label>
          <select v-model="form.backup"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            required>
            <option value="" disabled>Select Backup</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        <div v-if="showBank" class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Bank</label>
          <select
            v-model="form.bank"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
          >
            <option value="" disabled>
              {{ activeBankOptions.length === 0 ? 'No active banks registered' : 'All Banks' }}
            </option>
            <option
              disabled
              v-for="b in activeBankOptions"
              :key="b.id"
              :value="b.id"
            >
              {{ b.bankName }}
            </option>
          </select>
        </div>
      </div>

      <div class="mt-4 flex justify-center items-center gap-3">
        <button
          type="submit"
          :disabled="!canSaveCompanyProfile || isSaving || isLoadingProfile"
          class="min-w-[160px] px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
        >
          <div
            v-if="isSaving"
            class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isSaving">Save Company profile</span>
        </button>
        <!-- <span v-if="state.saved" class="text-xs font-semibold text-emerald-700">Saved.</span> -->
      </div>
    </form>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>
  </div>
</template>

<script setup>
import { computed, defineProps, onMounted, onBeforeUnmount, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const props = defineProps({
  apiBase: {
    type: String,
    default: '/finance'
  },
  showBank: {
    type: Boolean,
    default: true
  },
  skipPrivilegeCheck: {
    type: Boolean,
    default: false
  },
  privilegeName: {
    type: String,
    default: 'Company Profile'
  }
})

const store = useStore()

const profileApiPath = computed(() => `${props.apiBase}/company-profile`)

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canSaveCompanyProfile = computed(() => {
  if (props.skipPrivilegeCheck) return true
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), 'Edit')
})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const form = reactive({
  accountingType: '',
  accountingPeriod: '',
  startDate: '',
  endDate: '',
  companyStatus: '',
  taxType: '',
  postingMethod: '',
  backup: '',
  bank: ''
})

const state = reactive({
  saved: false
})

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
const isSaving = ref(false)
const isLoadingProfile = ref(false)
const hasExistingProfile = ref(false)

const activeBankOptions = ref([])

function bankRowIsActive(row) {
  if (!row || typeof row !== 'object') return false
  const a = row.active
  return a === true || a === 1 || a === '1'
}

async function loadActiveBankOptions() {
  if (!props.showBank) {
    activeBankOptions.value = []
    return
  }
  try {
    const res = await apiFetch('/finance/bank-register')
    if (!res.ok) {
      activeBankOptions.value = []
      return
    }
    const data = await res.json().catch(() => [])
    const list = Array.isArray(data) ? data : []
    activeBankOptions.value = list
      .filter(bankRowIsActive)
      .map((row) => ({
        id: String(row.id),
        bankName:
          row.bankName != null && String(row.bankName).trim() !== ''
            ? String(row.bankName).trim()
            : '—',
      }))
      .filter((row) => row.id)
  } catch {
    activeBankOptions.value = []
  }
}

function syncStoredBankIfInvalid() {
  if (!form.bank) return
  if (!activeBankOptions.value.some((b) => b.id === String(form.bank))) {
    form.bank = ''
  }
}

async function refreshBanks() {
  if (!props.showBank) return
  await loadActiveBankOptions()
  syncStoredBankIfInvalid()
}

function showToast(message, variant = 'success') {
  toast.message = message
  toast.variant = variant
  toast.show = true
  window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
  }, 1600)
}

function loadPersisted() {
  return loadFromApi()
}

async function loadFromApi() {
  isLoadingProfile.value = true
  try {
    const response = await apiFetch(profileApiPath.value)
    if (!response.ok) {
      hasExistingProfile.value = false
      return
    }
    const parsed = await response.json().catch(() => null)
    if (!parsed || typeof parsed !== 'object') {
      hasExistingProfile.value = false
      return
    }
    hasExistingProfile.value = true
    for (const key of Object.keys(form)) {
      if (key === 'bank') continue
      if (key in parsed) form[key] = parsed[key] ?? ''
    }
  } catch {
    showToast('Failed to load company profile.', 'error')
  } finally {
    isLoadingProfile.value = false
  }
}

async function save() {
  if (!canSaveCompanyProfile.value || isSaving.value || isLoadingProfile.value) return
  isSaving.value = true
  state.saved = false
  try {
    const payload = {
      accountingType: form.accountingType,
      accountingPeriod: form.accountingPeriod,
      startDate: form.startDate,
      endDate: form.endDate,
      companyStatus: form.companyStatus,
      taxType: form.taxType,
      postingMethod: form.postingMethod,
      backup: form.backup
    }
    const response = await apiFetch(profileApiPath.value, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      showToast(err.message || 'Failed to save company profile.', 'error')
      state.saved = false
      return
    }
    state.saved = true
    showToast(
      hasExistingProfile.value ? 'Company profile updated successfully.' : 'Company profile saved successfully.',
      'success'
    )
    await loadFromApi()
    window.setTimeout(() => {
      state.saved = false
    }, 1200)
  } catch {
    showToast('Failed to save company profile.', 'error')
    state.saved = false
  } finally {
    isSaving.value = false
  }
}

function onWindowFocus() {
  refreshBanks()
}

onMounted(async () => {
  if (props.showBank) {
    await loadActiveBankOptions()
  }
  await loadPersisted()
  if (props.showBank) {
    syncStoredBankIfInvalid()
    window.addEventListener('focus', onWindowFocus)
  }
})

onBeforeUnmount(() => {
  if (props.showBank) {
    window.removeEventListener('focus', onWindowFocus)
  }
})
</script>
